import React from 'react';

import { CheckCircle, HourglassEmpty } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Clear';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { MakeMeAnOfferActionType, MakeMeAnOfferStepStatus } from '~/services/AccommodationService';

export interface MakeMeAnOfferCreateStatusProps {
  name?: string;
  offerUrl?: string;
  errorMessage?: string;
  actionType?: MakeMeAnOfferActionType | null;
  status: {
    // Clone flow fields
    validateOffer?: MakeMeAnOfferStepStatus;
    cloneVendor?: MakeMeAnOfferStepStatus;
    cloneProperty?: MakeMeAnOfferStepStatus;
    cloneOffer?: MakeMeAnOfferStepStatus;
    // Upsert flow fields
    validateProperty?: MakeMeAnOfferStepStatus;
    validateVendor?: MakeMeAnOfferStepStatus;
    upsertVendor?: MakeMeAnOfferStepStatus;
    upsertProperty?: MakeMeAnOfferStepStatus;
    upsertOffer?: MakeMeAnOfferStepStatus;
    // LPC-specific flow fields
    waitForChannelManagerSync?: MakeMeAnOfferStepStatus;
    insertARI?: MakeMeAnOfferStepStatus;
    // Always last step
    resyncOffer?: MakeMeAnOfferStepStatus;
  };
}

export default function MakeMeAnOfferCreateStatus({
  name,
  offerUrl,
  errorMessage,
  actionType,
  status,
}: MakeMeAnOfferCreateStatusProps) {
  const getSteps = () => {
    switch (actionType) {
      case 'upsert-prod-flash-offer':
        return [
          { label: 'Validate Offer', status: status.validateOffer },
          { label: 'Validate Property', status: status.validateProperty },
          { label: 'Validate Vendor', status: status.validateVendor },
          { label: 'Upsert Vendor', status: status.upsertVendor },
          { label: 'Upsert Property', status: status.upsertProperty },
          { label: 'Upsert Offer', status: status.upsertOffer },
          { label: 'Resync Offer', status: status.resyncOffer },
        ];
      case 'upsert-prod-lpc-offer':
        return [
          { label: 'Validate Offer', status: status.validateOffer },
          { label: 'Validate Property', status: status.validateProperty },
          { label: 'Validate Vendor', status: status.validateVendor },
          { label: 'Upsert Vendor', status: status.upsertVendor },
          { label: 'Upsert Property', status: status.upsertProperty },
          { label: 'Upsert Offer', status: status.upsertOffer },
          { label: 'Wait for Channel Manager Sync', status: status.waitForChannelManagerSync },
          { label: 'Insert ARI', status: status.insertARI },
          { label: 'Resync Offer', status: status.resyncOffer },
        ];
      case 'clone-flash-offer':
        return [
          { label: 'Validate Offer', status: status.validateOffer },
          { label: 'Clone Vendor', status: status.cloneVendor },
          { label: 'Clone Property', status: status.cloneProperty },
          { label: 'Clone Offer', status: status.cloneOffer },
          { label: 'Resync Offer', status: status.resyncOffer },
        ];
      case 'clone-lpc-offer':
        return [
          { label: 'Validate Offer', status: status.validateOffer },
          { label: 'Clone Vendor', status: status.cloneVendor },
          { label: 'Clone Property', status: status.cloneProperty },
          { label: 'Clone Offer', status: status.cloneOffer },
          { label: 'Wait for Channel Manager Sync', status: status.waitForChannelManagerSync },
          { label: 'Insert ARI', status: status.insertARI },
          { label: 'Resync Offer', status: status.resyncOffer },
        ];
      default:
        return [];
    }
  };

  const steps = getSteps();

  const allCompleted = steps.length > 0 && steps.every((step) => step.status === 'completed');

  return (
    <Box>
      <Typography variant="h6">Offer Creation Status</Typography>

      <List>
        {steps.map((step, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {step.status === 'completed' && <CheckCircle color="success" />}
              {step.status === 'awaiting' && <HourglassEmpty color="disabled" />}
              {step.status === 'processing' && <CircularProgress size={20} />}
              {step.status === 'failed' && <DeleteIcon color="error" />}
            </ListItemIcon>
            <ListItemText primary={step.label} />
          </ListItem>
        ))}
      </List>

      {allCompleted && (
        <Alert severity="success">
          <AlertTitle>Offer Successfully Created!</AlertTitle>
          <Link href={offerUrl} target="_blank">
            {name}
          </Link>
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
}
